'use client';

import { useEffect, useState } from 'react';

import { getUtag } from '../core';
import {
  EVENT_CONSENT_CHANGED,
  EVENT_TRACKER_READY,
} from '../utils/event-names';

import { useUtag } from './use-utag';

import type { ConsentCategory } from '../types';

type UseConsentCategoriesHook = () => {
  hasConsent: (category: ConsentCategory) => boolean | undefined;
  setConsent: (category: ConsentCategory, consentGiven: boolean) => void;
};

/**
 * Use cookie consent categories.
 *
 * ```tsx
 * let { hasConsent, setConsent } = useConsentCategories();
 *
 * let hasAffiliatesConsent = hasConsent('affiliates');
 *
 * let handleClick = () => {
 *   setConsent('affiliates', true');
 * }
 * ```
 */
export const useConsentCategories: UseConsentCategoriesHook = () => {
  const [selectedCategories, setSelectedCategories] = useState<
    ConsentCategory[]
  >([]);

  const handleConsentChange = () => {
    try {
      setSelectedCategories(
        getUtag(window)?.gdpr.getSelectedCategories() ?? [],
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Could not set selected consent categories in Utag.', e);
    }
  };

  useEffect(() => {
    window.addEventListener(EVENT_CONSENT_CHANGED, handleConsentChange);
    window.addEventListener(EVENT_TRACKER_READY, handleConsentChange);

    return () => {
      window.removeEventListener(EVENT_CONSENT_CHANGED, handleConsentChange);
      window.removeEventListener(EVENT_TRACKER_READY, handleConsentChange);
    };
  }, []);

  useUtag(() => {
    handleConsentChange();
  }, []);

  return {
    hasConsent: category => selectedCategories.includes(category),
    setConsent: (category, consentGiven) => {
      const utag = getUtag(window);

      if (!utag) {
        return;
      }

      let existing: ConsentCategory[] = [];

      try {
        existing = utag.gdpr.getSelectedCategories();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          'Could not get selected consent categories from Utag.',
          e,
        );
        existing = [];
      }

      if (!consentGiven) {
        existing = existing.filter(
          existingCategory => existingCategory !== category,
        );
      } else {
        existing = [...new Set([...existing, category])];
      }

      try {
        utag.gdpr.setPreferencesFromList(existing);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Could not set preferences in Utag.', e);
      }

      setSelectedCategories(existing);
      handleConsentChange();
      window.dispatchEvent(new Event(EVENT_CONSENT_CHANGED));
    },
  };
};
